import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTENT_SCHEMA } from "@/lib/contentSchema";
import { DEFAULT_CONTENT } from "@/lib/contentDefaults";
import { Save, RotateCcw, Loader2, FileText, ExternalLink, ImagePlus, Copy, Images, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;

const serialize = (field, value) => {
  if (field.type === "roomCards") return value || [];
  if (field.type === "lines") return (value || []).join("\n");
  if (field.type === "objects")
    return (value || [])
      .map((o) => field.fields.map((f) => (Array.isArray(o[f]) ? o[f].join("; ") : o[f] ?? "")).join(" | "))
      .join("\n");
  return value ?? "";
};

const parse = (field, text) => {
  if (field.type === "roomCards") return text;
  if (field.type === "lines") return text.split("\n").map((s) => s.trim()).filter(Boolean);
  if (field.type === "objects")
    return text.split("\n").map((s) => s.trim()).filter(Boolean).map((line) => {
      const parts = line.split("|").map((p) => p.trim());
      const obj = {};
      field.fields.forEach((f, i) => {
        const v = parts[i] ?? "";
        obj[f] = f === "points" ? v.split(";").map((p) => p.trim()).filter(Boolean) : v;
      });
      return obj;
    });
  return text;
};

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#024AD8] focus:ring-4 focus:ring-[#024AD8]/15";

export default function ContentEditor({ token }) {
  const sections = Object.keys(CONTENT_SCHEMA);
  const [content, setContent] = useState(null);
  const [section, setSection] = useState("hero");
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const fileInputRef = useRef(null);
  const pendingFieldRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchUploads = () =>
    axios.get(`${API}/admin/files`, { headers: authHeaders }).then(({ data }) => setUploads(data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/content`).then(({ data }) => setContent(data.content || {}));
    fetchUploads();
  }, []);

  useEffect(() => {
    if (content === null) return;
    const merged = { ...DEFAULT_CONTENT[section], ...(content[section] || {}) };
    const next = {};
    CONTENT_SCHEMA[section].fields.forEach((f) => {
      next[f.key] = serialize(f, merged[f.key]);
    });
    setValues(next);
  }, [section, content]);

  const save = async (sectionKey, vals) => {
    setSaving(true);
    try {
      const sectionObj = {};
      CONTENT_SCHEMA[sectionKey].fields.forEach((f) => {
        sectionObj[f.key] = parse(f, vals[f.key]);
      });
      const newContent = { ...content, [sectionKey]: sectionObj };
      await axios.put(`${API}/admin/content`, { content: newContent }, { headers: authHeaders });
      setContent(newContent);
      toast.success("Saved — live on the site now.");
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Session expired — sign in again." : "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const newContent = { ...content };
      delete newContent[section];
      await axios.put(`${API}/admin/content`, { content: newContent }, { headers: authHeaders });
      setContent(newContent);
      toast.success("Section restored to default content.");
    } catch (e) {
      toast.error("Could not reset section.");
    } finally {
      setSaving(false);
    }
  };

  const pickFile = (fieldKey, roomIndex = null) => {
    pendingFieldRef.current = fieldKey === null ? null : { fieldKey, roomIndex };
    fileInputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB.");
      return;
    }
    const pending = pendingFieldRef.current;
    setUploadingFor(pending ? (pending.roomIndex != null ? `room-${pending.roomIndex}` : pending.fieldKey) : "library");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, form, { headers: authHeaders });
      const url = fileUrl(data.path);
      if (pending?.roomIndex != null) {
        setValues((v) => ({
          ...v,
          [pending.fieldKey]: v[pending.fieldKey].map((r, i) => (i === pending.roomIndex ? { ...r, img: url } : r)),
        }));
      } else if (pending?.fieldKey) {
        setValues((v) => ({ ...v, [pending.fieldKey]: url }));
      }
      await fetchUploads();
      toast.success(pending ? "Photo uploaded and placed — save to publish." : "Photo uploaded to the media library.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploadingFor(null);
    }
  };

  const copyUrl = (path) => {
    navigator.clipboard.writeText(fileUrl(path));
    toast.success("Image URL copied — paste it into any photo field.");
  };

  if (content === null) {
    return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>;
  }

  const isCustomized = Boolean(content[section]);

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-6 items-start">
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleFile} />

      <div className="rounded-2xl border border-slate-200 bg-white p-3 lg:sticky lg:top-6">
        <p className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400">Sections</p>
        <div className="space-y-1">
          {sections.map((key) => (
            <button
              key={key}
              data-testid={`content-section-${key}`}
              onClick={() => setSection(key)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-between gap-2 ${
                section === key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {CONTENT_SCHEMA[key].label}
              {content[key] && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${section === key ? "bg-[#6D8FF0]" : "bg-[#024AD8]"}`} />}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
                <FileText className="w-5 h-5 text-[#024AD8]" />
                {CONTENT_SCHEMA[section].label}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {isCustomized ? "This section has custom content." : "Showing default content — edit and save to customize."}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                data-testid="media-library-toggle"
                onClick={() => setShowLibrary((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              >
                <Images className="w-3.5 h-3.5" /> Media uploads ({uploads.length})
              </button>
              <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#024AD8] hover:text-[#0238A8]">
                View live site <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {showLibrary && (
            <div data-testid="media-library" className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Uploaded photos</p>
                <button
                  data-testid="media-upload-button"
                  onClick={() => pickFile(null)}
                  disabled={uploadingFor !== null}
                  className="inline-flex items-center gap-2 bg-slate-900 hover:bg-[#024AD8] disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors duration-300"
                >
                  {uploadingFor === "library" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                  Upload photo
                </button>
              </div>
              {uploads.length === 0 ? (
                <p className="text-sm text-slate-500">No uploads yet. Uploaded photos can be copied into any photo field.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {uploads.map((u) => (
                    <div key={u.id} className="group relative rounded-lg overflow-hidden border border-slate-200 bg-white">
                      <img src={fileUrl(u.storage_path)} alt={u.original_filename} className="w-full h-20 object-cover" loading="lazy" />
                      <button
                        onClick={() => copyUrl(u.storage_path)}
                        className="absolute inset-0 bg-slate-900/70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 grid place-items-center text-white text-xs font-semibold"
                      >
                        <span className="inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy URL</span>
                      </button>
                      <p className="px-2 py-1.5 text-[10px] text-slate-500 truncate">{u.original_filename}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-7 space-y-6">
            {CONTENT_SCHEMA[section].fields.map((field) => (
              <div key={field.key}>
                <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">{field.label}</label>
                {field.type === "image" && values[field.key] ? (
                  <img src={values[field.key]} alt="" className="mb-2 h-24 rounded-lg object-cover border border-slate-200" onError={(e) => (e.currentTarget.style.display = "none")} />
                ) : null}
                {field.type === "roomCards" ? (
                <div className="space-y-3">
                  {(values[field.key] || []).map((room, idx) => (
                    <div key={idx} data-testid={`room-editor-${idx}`} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                      <div className="flex items-start gap-3">
                        {room.img ? (
                          <img src={room.img} alt="" className="w-20 h-14 object-cover rounded-lg border border-slate-200 shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
                        ) : (
                          <div className="w-20 h-14 rounded-lg border border-dashed border-slate-300 grid place-items-center text-slate-400 shrink-0">
                            <ImagePlus className="w-4 h-4" />
                          </div>
                        )}
                        <div className="flex-1 grid sm:grid-cols-3 gap-2.5">
                          <input
                            data-testid={`room-${idx}-type`}
                            className={inputClass}
                            placeholder="Room type"
                            value={room.type}
                            onChange={(e) => setValues((v) => ({ ...v, [field.key]: v[field.key].map((r, i) => (i === idx ? { ...r, type: e.target.value } : r)) }))}
                          />
                          <input
                            data-testid={`room-${idx}-device`}
                            className={inputClass}
                            placeholder="Device"
                            value={room.device}
                            onChange={(e) => setValues((v) => ({ ...v, [field.key]: v[field.key].map((r, i) => (i === idx ? { ...r, device: e.target.value } : r)) }))}
                          />
                          <input
                            data-testid={`room-${idx}-img`}
                            className={inputClass}
                            placeholder="Photo URL or upload →"
                            value={room.img}
                            onChange={(e) => setValues((v) => ({ ...v, [field.key]: v[field.key].map((r, i) => (i === idx ? { ...r, img: e.target.value } : r)) }))}
                          />
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <button
                            data-testid={`room-${idx}-upload`}
                            onClick={() => pickFile(field.key, idx)}
                            disabled={uploadingFor !== null}
                            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-[#024AD8] disabled:opacity-60 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition-colors duration-300"
                          >
                            {uploadingFor === `room-${idx}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                            Upload
                          </button>
                          <button
                            data-testid={`room-${idx}-remove`}
                            onClick={() => setValues((v) => ({ ...v, [field.key]: v[field.key].filter((_, i) => i !== idx) }))}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 border border-slate-300 px-3.5 py-2 rounded-lg transition-colors duration-300"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    data-testid="room-add-button"
                    onClick={() => setValues((v) => ({ ...v, [field.key]: [...(v[field.key] || []), { type: "", device: "", img: "" }] }))}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#024AD8] border border-dashed border-[#024AD8]/50 px-4 py-2.5 rounded-lg hover:bg-[#EAF0FC] transition-colors duration-300"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add room card
                  </button>
                </div>
              ) : field.type === "text" || field.type === "image" ? (
                  <div className="flex gap-2">
                    <input
                      data-testid={`content-field-${field.key}`}
                      className={inputClass}
                      value={values[field.key] ?? ""}
                      onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                    />
                    {field.type === "image" && (
                      <button
                        data-testid={`content-upload-${field.key}`}
                        onClick={() => pickFile(field.key)}
                        disabled={uploadingFor !== null}
                        className="shrink-0 inline-flex items-center gap-2 bg-slate-900 hover:bg-[#024AD8] disabled:opacity-60 text-white text-xs font-semibold px-4 rounded-xl transition-colors duration-300"
                      >
                        {uploadingFor === field.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                        Upload
                      </button>
                    )}
                  </div>
                ) : (
                  <textarea
                    data-testid={`content-field-${field.key}`}
                    rows={field.type === "textarea" ? 4 : field.type === "lines" ? 5 : 6}
                    className={`${inputClass} resize-y font-normal`}
                    value={values[field.key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <button
              data-testid="content-save-button"
              onClick={() => save(section, values)}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#024AD8] hover:bg-[#0238A8] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-colors duration-300"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </button>
            <button
              data-testid="content-reset-button"
              onClick={reset}
              disabled={saving || !isCustomized}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 border border-slate-300 px-5 py-3 rounded-xl hover:bg-slate-100 disabled:opacity-50 transition-colors duration-300"
            >
              <RotateCcw className="w-4 h-4" /> Reset to default
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
